import { useMemo } from "react"

import { useQuery } from "@tanstack/react-query"

import { listSegmentCompanies } from "../api/company-analysis-api"

/**
 * Resolve CCP summary blurbs for a set of customer/prospect names in a segment.
 *
 * One `listSegmentCompanies` call, which now carries each company's `ccpSummary`
 * alongside its id and aliases. This used to fan out a `getCcp` per name with no
 * pagination or concurrency cap — and each of those did several storage and name
 * lookups server-side — so a segment with a dozen customers issued a dozen
 * parallel plan fetches on every render of the Customers tab.
 *
 * Returns a `name → summary` map containing only names whose CCP has a non-empty
 * summary, so callers can override the card description without extra guards.
 * Disabled entirely unless `enabled` (mock data source off).
 */
export function useCcpSummaries(
  segmentId: string,
  names: string[],
  enabled: boolean
): Record<string, string> {
  const companiesQuery = useQuery({
    queryKey: ["sa-ccp-summary-companies", segmentId],
    queryFn: () => listSegmentCompanies(segmentId, { includeSummaries: true }),
    enabled: enabled && Boolean(segmentId),
    staleTime: 5 * 60 * 1000,
  })

  const summaryByName = useMemo(() => {
    const map = new Map<string, string>()
    for (const company of companiesQuery.data?.companies ?? []) {
      const summary = company.ccpSummary?.trim()
      if (!summary) continue
      if (company.name) map.set(company.name.toLowerCase(), summary)
      for (const alias of company.aliases ?? []) map.set(alias.toLowerCase(), summary)
    }
    return map
  }, [companiesQuery.data])

  return useMemo(() => {
    const summaries: Record<string, string> = {}
    for (const name of names) {
      const summary = summaryByName.get(name.toLowerCase())
      if (summary) summaries[name] = summary
    }
    return summaries
  }, [names, summaryByName])
}
